import { useEffect, useRef, useState } from 'react';
import { ImagePlus, Loader2 } from 'lucide-react';
import { marked } from 'marked';
import TurndownService from 'turndown';
import { TiptapEditor, type Editor } from '@/v5/components/ui/tiptap-editor';
import { Textarea } from '@/components/ui/Textarea';
import { cn } from '@/lib/utils';

type Mode = 'visual' | 'html' | 'markdown';

interface Props {
  html: string;
  onChange: (html: string) => void;
  /**
   * Optional uploader.  When provided, the visual-mode toolbar shows an
   * "Upload Image" button that runs the picked file through this callback
   * and inserts the returned URL into the editor at the current cursor.
   * If omitted, only the existing TipTap "image by URL" toolbar button is
   * available.
   */
  onUploadImage?: (file: File) => Promise<string>;
}

const turndown = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced',
  bulletListMarker: '-',
});

const MODES: Array<{ id: Mode; label: string }> = [
  { id: 'visual', label: 'Visual' },
  { id: 'html', label: 'HTML' },
  { id: 'markdown', label: 'Markdown' },
];

export function BlogBodyEditor({ html, onChange, onUploadImage }: Props) {
  const [mode, setMode] = useState<Mode>('visual');
  const [mdSource, setMdSource] = useState('');
  const [uploading, setUploading] = useState(false);
  const editorRef = useRef<Editor | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const lastVisualHtmlRef = useRef<string>(html);

  async function handleFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !onUploadImage) return;
    const editor = editorRef.current;
    if (!editor) return;
    setUploading(true);
    try {
      const url = await onUploadImage(file);
      editor.chain().focus().setImage({ src: url }).run();
      lastVisualHtmlRef.current = editor.getHTML();
      onChange(editor.getHTML());
    } catch {
      // Caller is expected to surface a toast; we just don't insert anything.
    } finally {
      setUploading(false);
    }
  }

  useEffect(() => {
    if (mode !== 'visual') return;
    if (html === lastVisualHtmlRef.current) return;
    const editor = editorRef.current;
    if (!editor) return;
    editor.commands.setContent(html || '', { emitUpdate: false });
    lastVisualHtmlRef.current = html;
  }, [html, mode]);

  const handleVisualChange = (next: string) => {
    lastVisualHtmlRef.current = next;
    onChange(next);
  };

  const switchTo = (next: Mode) => {
    if (next === mode) return;
    if (next === 'markdown') {
      setMdSource(html ? turndown.turndown(html) : '');
    }
    if (next === 'visual') {
      lastVisualHtmlRef.current = html;
    }
    setMode(next);
  };

  return (
    <div className="space-y-2">
      <div role="tablist" className="flex items-center gap-1 border-b border-slate-200">
        {MODES.map((m) => {
          const active = m.id === mode;
          return (
            <button
              key={m.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => switchTo(m.id)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors',
                active
                  ? 'text-blue-600 border-blue-600'
                  : 'text-slate-500 border-transparent hover:text-slate-700',
              )}
            >
              {m.label}
            </button>
          );
        })}
        {onUploadImage && mode === 'visual' && (
          <div className="ml-auto flex items-center pr-1">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFilePicked}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className={cn(
                'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium',
                'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              {uploading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ImagePlus className="h-3.5 w-3.5" />
              )}
              {uploading ? 'Uploading…' : 'Upload Image'}
            </button>
          </div>
        )}
      </div>

      {mode === 'visual' && (
        <TiptapEditor
          content={html}
          onChange={handleVisualChange}
          placeholder="Write your post…"
          editorClassName="min-h-[280px]"
          editorRef={editorRef}
        />
      )}

      {mode === 'html' && (
        <Textarea
          rows={14}
          value={html}
          onChange={(e) => onChange(e.target.value)}
          placeholder="<p>Raw HTML…</p>"
          className="font-mono text-xs"
        />
      )}

      {mode === 'markdown' && (
        <Textarea
          rows={14}
          value={mdSource}
          onChange={(e) => {
            const next = e.target.value;
            setMdSource(next);
            const rendered = marked.parse(next, { async: false }) as string;
            onChange(rendered);
          }}
          placeholder="# Heading&#10;&#10;Write markdown here…"
          className="font-mono text-xs"
        />
      )}

      <p className="text-xs text-slate-500">
        {mode === 'visual' && 'Rich text editor — content is saved as HTML.'}
        {mode === 'html' && 'Editing raw HTML. Switch back to Visual to apply changes.'}
        {mode === 'markdown' && 'Markdown is converted to HTML on save. Switching tabs reseeds from the latest HTML.'}
      </p>
    </div>
  );
}
